import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    nome: '',
    empresa: '',
    telefone: '',
    email: '',
    mensagem: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (!formData.nome || !formData.empresa || !formData.telefone || !formData.email || !formData.mensagem) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.mensagem.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams({
        nome: formData.nome,
        empresa: formData.empresa,
        telefone: formData.telefone,
        email: formData.email,
        mensagem: formData.mensagem,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6120d4cm4ll99gupci0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          empresa: '',
          telefone: '',
          email: '',
          mensagem: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="relative bg-primary pt-32 pb-20 overflow-hidden">
        <div className="relative w-full px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.2em] text-white/40 uppercase mb-4">
              Contato
            </span>
            <h1 className="text-4xl lg:text-5xl font-medium text-white leading-tight mb-6">
              Fale Conosco
            </h1>
            <p className="text-white/60 text-lg">
              Retorno em até 24 horas úteis
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 lg:py-28">
        <div className="w-full px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">
            {/* Info Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-semibold text-primary mb-8">
                Entre em contato
              </h2>
              <p className="text-secondary text-base leading-relaxed mb-12">
                Preencha o formulário ao lado ou entre em contato diretamente pelo WhatsApp para um atendimento mais rápido.
              </p>

              {/* Contact Cards */}
              <div className="space-y-4">
                <a
                  href="https://wa.me/5541997438126"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-5 bg-light rounded-xl hover:bg-accent/30 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#25D366] flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-medium text-primary text-sm">WhatsApp</p>
                    <p className="text-secondary text-sm">(41) 99743-8126</p>
                  </div>
                  <i className="ri-arrow-right-up-line text-secondary/40 ml-auto group-hover:text-primary transition-colors duration-300"></i>
                </a>

                <div className="flex items-center gap-4 p-5 bg-light rounded-xl">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <p className="font-medium text-primary text-sm">Atendimento</p>
                    <p className="text-secondary text-sm">Seg - Sex, 8h às 18h</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-5 bg-light rounded-xl">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <p className="font-medium text-primary text-sm">Localização</p>
                    <p className="text-secondary text-sm">Curitiba, PR</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <div className="bg-light rounded-2xl p-8 lg:p-12">
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="nome"
                        className="block text-xs font-medium tracking-wider text-secondary uppercase mb-3"
                      >
                        Nome *
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                        className="w-full border-0 bg-white rounded-lg px-5 py-4 text-sm text-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all duration-300"
                        placeholder="Seu nome"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="empresa"
                        className="block text-xs font-medium tracking-wider text-secondary uppercase mb-3"
                      >
                        Empresa *
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        required
                        className="w-full border-0 bg-white rounded-lg px-5 py-4 text-sm text-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all duration-300"
                        placeholder="Nome da empresa"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="telefone"
                        className="block text-xs font-medium tracking-wider text-secondary uppercase mb-3"
                      >
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        required
                        className="w-full border-0 bg-white rounded-lg px-5 py-4 text-sm text-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all duration-300"
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium tracking-wider text-secondary uppercase mb-3"
                      >
                        E-mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full border-0 bg-white rounded-lg px-5 py-4 text-sm text-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all duration-300"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>

                  <div className="mb-8">
                    <label
                      htmlFor="mensagem"
                      className="block text-xs font-medium tracking-wider text-secondary uppercase mb-3"
                    >
                      Mensagem *
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full border-0 bg-white rounded-lg px-5 py-4 text-sm text-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all duration-300 resize-none"
                      placeholder="Descreva sua necessidade..."
                    ></textarea>
                    <p className="text-secondary/60 text-xs mt-2">
                      {formData.mensagem.length}/500 caracteres
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-white font-semibold text-sm py-5 rounded-lg hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Mensagem
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-3"
                    >
                      <i className="ri-checkbox-circle-fill text-xl"></i>
                      <span className="text-sm">Mensagem enviada com sucesso! Retornaremos em até 24 horas.</span>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-3"
                    >
                      <i className="ri-error-warning-fill text-xl"></i>
                      <span className="text-sm">Erro ao enviar. Verifique os campos e tente novamente.</span>
                    </motion.div>
                  )}
                </form>

                <p className="text-secondary/60 text-xs mt-6 text-center">
                  Ao enviar, você concorda com nossa política de privacidade.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Botão Flutuante WhatsApp */}
      <a
        href="https://wa.me/5541997438126"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-[#25D366] text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-[#20BA5A] hover:scale-110 transition-all duration-300 z-50 cursor-pointer"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
};

export default Contact;
